<template>
  <vinnuskoli-page>
    <div class="page-title mb-3">Launataxti</div>
    <b-tabs @activate-tab="onActivateTab">
      <b-tab title="Launataxtar" active>
        <b-row class="mb-3 mt-3">
          <b-col class="d-flex align-items-center">
            <p class="text-muted small mb-0">
              Tímakaup og launaliður eftir aldursbilum og tímabili. Launaliður ákvarðar hvaða launategund er notuð þegar klukkustundir eru
              sendar í launakerfi.
            </p>
          </b-col>
          <b-col
            class="d-flex justify-content-end align-items-center flex-grow-0 flex-shrink-0"
            style="white-space: nowrap"
            v-if="loggedInUserHasAnyPermission('write_wageperiod')"
          >
            <b-btn variant="primary" @click.prevent="wageperiodCreateModal" class="pull-right">
              <i class="fa fa-fw fa-plus-circle"></i>
              Bæta við
            </b-btn>
          </b-col>
        </b-row>
        <b-card no-body border-variant="light" class="mb-2">
          <table class="table mb-0">
            <thead>
              <tr class="bg-gray-200 small">
                <th>Dags. frá</th>
                <th>Dags. til</th>
                <th>Tímakaup</th>
                <th></th>
              </tr>
            </thead>
            <tbody v-if="loading">
              <td colspan="100"><loading /></td>
            </tbody>
            <tbody v-if="wageperiodList.length == 0 && !loading">
              <td colspan="100"><i>Engar leitarniðurstöður fundust.</i></td>
            </tbody>
            <tbody v-if="!loading && wageperiodList.length > 0">
              <tr v-for="period in wageperiodList" :key="period.id">
                <td>{{ period.date_from | moment('DD.MM.YYYY') }}</td>
                <td>{{ period.date_to | moment('DD.MM.YYYY') }}</td>
                <td class="p-0">
                  <table class="table table-no-border table-hover">
                    <tr>
                      <th style="width: 35%">Aldur</th>
                      <th style="width: 35%">Launaliður</th>
                      <th style="width: 15%">Veikindi</th>
                      <th style="width: 15%" class="text-right">Tímakaup</th>
                    </tr>
                    <tbody>
                      <tr v-for="(age, index) in period.ages" :key="index">
                        <td>{{ age.age }} ára ({{ getYear(period, age.age) }})</td>
                        <td>{{ age.wage_element || period.wage_element }}</td>
                        <td>
                          <span v-if="age.sick_days_enabled" class="badge badge-info">Já</span>
                          <span v-if="age.sick_days_enabled && age.sick_wage_element" class="ml-1 small text-muted">{{
                            age.sick_wage_element
                          }}</span>
                        </td>
                        <td class="text-right">{{ age.wage | currency }}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
                <td width="10%">
                  <b-btn
                    variant="primary"
                    @click.prevent="wageperiodEditModal(period.id)"
                    class="pull-right"
                    v-if="loggedInUserHasAnyPermission('write_wageperiod') && isPeriodOver(period.date_to)"
                  >
                    <i class="fa fa-fw fa-pencil"></i>
                  </b-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </b-card>
      </b-tab>

      <b-tab title="Launaflokkar H3" v-if="hasH3">
        <div class="mt-3">
          <p class="text-muted small mb-3">
            Starfsmannaflokkar sem sendir eru til H3 við skráningu þátttakenda, skipt niður eftir fæðingarári. Stillingar hér ákvarða
            starfsheiti, launatöflu, launaflokk og þrep í H3 kerfinu.
          </p>
          <b-card no-body border-variant="light" class="mb-2">
            <table class="table mb-0">
              <thead>
                <tr class="bg-gray-200 small">
                  <th>Fæðingarár</th>
                  <th title="jobNameCode">Starfsheiti</th>
                  <th title="unionConCode">Launatafla</th>
                  <th title="conLevelName">Launaflokkur</th>
                  <th title="conStepName">Þrep</th>
                  <th v-if="loggedInUserHasAnyPermission('write_wageperiod')"></th>
                </tr>
              </thead>
              <tbody v-if="h3YearLoading">
                <tr>
                  <td colspan="100"><loading /></td>
                </tr>
              </tbody>
              <tbody v-else-if="h3YearList.length === 0">
                <tr>
                  <td colspan="100"><i>Engar færslur fundust.</i></td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr v-for="row in h3YearList" :key="row.id">
                  <td>{{ row.birthYear }}</td>
                  <td>{{ row.jobNameCode }}</td>
                  <td>{{ row.unionConCode }}</td>
                  <td>{{ row.conLevelName }}</td>
                  <td>{{ row.conStepName }}</td>
                  <td v-if="loggedInUserHasAnyPermission('write_wageperiod')" class="text-right">
                    <b-btn variant="danger" size="sm" @click.prevent="deleteH3Year(row)">
                      <i class="fa fa-fw fa-trash"></i>
                    </b-btn>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="loggedInUserHasAnyPermission('write_wageperiod')">
                <tr>
                  <td>
                    <b-form-input v-model.number="newH3Year.birth_year" type="number" placeholder="Fæðingarár" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model="newH3Year.job_name_code" placeholder="Starfsheiti" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model="newH3Year.union_con_code" placeholder="Launatafla" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model="newH3Year.con_level_name" placeholder="Launaflokkur" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model="newH3Year.con_step_name" placeholder="Þrep" size="sm" />
                  </td>
                  <td class="text-right">
                    <b-btn variant="primary" size="sm" @click.prevent="createH3Year" :disabled="h3YearSaving">
                      <i class="fa fa-fw fa-plus-circle"></i>
                    </b-btn>
                  </td>
                </tr>
              </tfoot>
            </table>
          </b-card>
        </div>
      </b-tab>

      <b-tab title="Launaflokkar Kjarni" v-if="hasKjarni">
        <div class="mt-3">
          <p class="text-muted small mb-3">
            Launatafla og launaflokkur í Kjarna eftir fæðingarári þátttakenda. Þessar stillingar ákvarða hvaða launatöflu, þrep og
            stéttarfélag er notað í Kjarna fyrir hvern fæðingarársflokk.
          </p>
          <b-card no-body border-variant="light" class="mb-2">
            <table class="table mb-0">
              <thead>
                <tr class="bg-gray-200 small">
                  <th>Fæðingarár</th>
                  <th title="PayTableID">Launatafla</th>
                  <th title="PayTableStep">Þrep</th>
                  <th title="PayTableGrade">Launaflokkur</th>
                  <th title="PayUnionID">Stéttarfélag</th>
                  <th v-if="loggedInUserHasAnyPermission('write_wageperiod')"></th>
                </tr>
              </thead>
              <tbody v-if="kjarniYearLoading">
                <tr>
                  <td colspan="100"><loading /></td>
                </tr>
              </tbody>
              <tbody v-else-if="kjarniYearList.length === 0">
                <tr>
                  <td colspan="100"><i>Engar færslur fundust.</i></td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr v-for="row in kjarniYearList" :key="row.id">
                  <td>{{ row.birthYear }}</td>
                  <td>{{ row.payTableId }}</td>
                  <td>{{ row.payTableStep }}</td>
                  <td>{{ row.payTableGrade }}</td>
                  <td>{{ row.payUnionId }}</td>
                  <td v-if="loggedInUserHasAnyPermission('write_wageperiod')" class="text-right">
                    <b-btn variant="danger" size="sm" @click.prevent="deleteKjarniYear(row)">
                      <i class="fa fa-fw fa-trash"></i>
                    </b-btn>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="loggedInUserHasAnyPermission('write_wageperiod')">
                <tr>
                  <td>
                    <b-form-input v-model.number="newKjarniYear.birth_year" type="number" placeholder="Fæðingarár" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model.number="newKjarniYear.pay_table_id" type="number" placeholder="Launatafla" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model.number="newKjarniYear.pay_table_step" type="number" placeholder="Þrep" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model.number="newKjarniYear.pay_table_grade" type="number" placeholder="Launaflokkur" size="sm" />
                  </td>
                  <td>
                    <b-form-input v-model="newKjarniYear.pay_union_id" placeholder="Stéttarfélag" size="sm" />
                  </td>
                  <td class="text-right">
                    <b-btn variant="primary" size="sm" @click.prevent="createKjarniYear" :disabled="kjarniYearSaving">
                      <i class="fa fa-fw fa-plus-circle"></i>
                    </b-btn>
                  </td>
                </tr>
              </tfoot>
            </table>
          </b-card>
        </div>
      </b-tab>
    </b-tabs>

    <wage-period-create-modal ref="wageperiodCreateModal" @successful="loadList"></wage-period-create-modal>
    <wage-period-create-modal ref="wageperiodEditModal" @successful="loadList"></wage-period-create-modal>
  </vinnuskoli-page>
</template>
<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import payment from '@/api/payment';
import WagePeriodCreateModal from '@/components/wageperiod/WagePeriodCreateModal.vue';

export default {
  name: 'wage-period',
  components: {
    WagePeriodCreateModal,
  },
  computed: {
    ...mapGetters(['loggedInUserHasAnyPermission', 'loggedInUserOptions']),
    hasH3() {
      return this.$store.state.loggedInUser?.town?.hasH3 === true;
    },
    hasKjarni() {
      return this.$store.state.loggedInUser?.town?.hasKjarni === true;
    },
  },
  data() {
    return {
      wageperiodList: [],
      loading: false,
      h3YearList: [],
      h3YearLoading: false,
      h3YearSaving: false,
      newH3Year: { birth_year: null, job_name_code: '', union_con_code: '', con_level_name: '', con_step_name: '' },
      kjarniYearList: [],
      kjarniYearLoading: false,
      kjarniYearSaving: false,
      newKjarniYear: { birth_year: null, pay_table_id: null, pay_table_step: null, pay_table_grade: null, pay_union_id: '' },
    };
  },
  methods: {
    async loadList() {
      this.loading = true;
      this.wageperiodList = [];
      try {
        const response = await payment.wagePeriodList();
        this.wageperiodList = response.data;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loading = false;
      }
    },
    async loadH3YearList() {
      this.h3YearLoading = true;
      this.h3YearList = [];
      try {
        const response = await payment.townH3InfoYearList();
        this.h3YearList = response.data.results;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.h3YearLoading = false;
      }
    },
    async createH3Year() {
      if (!this.newH3Year.birth_year) return;
      this.h3YearSaving = true;
      try {
        await payment.townH3InfoYearCreate(this.newH3Year);
        this.newH3Year = { birth_year: null, job_name_code: '', union_con_code: '', con_level_name: '', con_step_name: '' };
        await this.loadH3YearList();
        this.displaySuccess('Færsla bætt við.');
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.h3YearSaving = false;
      }
    },
    async deleteH3Year(row) {
      const conf = confirm(`Ertu viss um að þú viljir eyða launaflokk fyrir fæðingarár ${row.birthYear}?`); //eslint-disable-line
      if (!conf) return;
      this.h3YearSaving = true;
      try {
        await payment.townH3InfoYearDelete(row.id);
        await this.loadH3YearList();
        this.displaySuccess('Færslu eytt.');
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.h3YearSaving = false;
      }
    },
    async loadKjarniYearList() {
      this.kjarniYearLoading = true;
      this.kjarniYearList = [];
      try {
        const response = await payment.townKjarniInfoYearList();
        this.kjarniYearList = response.data.results;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.kjarniYearLoading = false;
      }
    },
    async createKjarniYear() {
      if (!this.newKjarniYear.birth_year) return;
      this.kjarniYearSaving = true;
      try {
        await payment.townKjarniInfoYearCreate(this.newKjarniYear);
        this.newKjarniYear = { birth_year: null, pay_table_id: null, pay_table_step: null, pay_table_grade: null, pay_union_id: '' };
        await this.loadKjarniYearList();
        this.displaySuccess('Færsla bætt við.');
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.kjarniYearSaving = false;
      }
    },
    async deleteKjarniYear(row) {
      const conf = confirm(`Ertu viss um að þú viljir eyða launaflokk fyrir fæðingarár ${row.birthYear}?`); //eslint-disable-line
      if (!conf) return;
      this.kjarniYearSaving = true;
      try {
        await payment.townKjarniInfoYearDelete(row.id);
        await this.loadKjarniYearList();
        this.displaySuccess('Færslu eytt.');
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.kjarniYearSaving = false;
      }
    },
    isPeriodOver(dateTo) {
      return moment(dateTo).isAfter(moment());
    },
    getYear(wage, age) {
      if (age) {
        let year = moment();
        if (wage && wage.date_to) {
          year = wage.date_to;
        }
        const born = moment(year).subtract(age, 'year');
        return born.format('YYYY');
      }
      return '';
    },
    onActivateTab(newTabIndex) {
      const h3Index = this.hasH3 ? 1 : -1;
      let kjarniIndex = -1;
      if (this.hasKjarni) kjarniIndex = this.hasH3 ? 2 : 1;
      if (newTabIndex === h3Index && this.h3YearList.length === 0) this.loadH3YearList();
      else if (newTabIndex === kjarniIndex && this.kjarniYearList.length === 0) {
        this.loadKjarniYearList();
      }
    },
    wageperiodCreateModal() {
      this.$refs.wageperiodCreateModal.show();
    },
    wageperiodEditModal(id) {
      this.$refs.wageperiodEditModal.show(id);
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
    }),
  },
  created() {
    this.loadList();
  },
};
</script>

<style lang="scss">
.table.table-no-border {
  tr {
    td,
    th {
      border-color: transparent;
    }
  }
}
</style>
